import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import EventPaymentDialog from '../shared/EventPaymentDialog';

// Cargar Stripe (usar tu clave pública de Stripe)
const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY) : null;

const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#7ED321',
  background: '#F8FDF8',
  surface: '#FFFFFF',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D',
  }
};

const UserEventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [hasAmpaPayment, setHasAmpaPayment] = useState(false);
  const [checkingAmpaPayment, setCheckingAmpaPayment] = useState(true);

  useEffect(() => {
    fetchEvent();
    checkAmpaPayment();
  }, [eventId]);

  // Función para obtener el año académico actual
  const getCurrentAcademicYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;

    return month >= 9 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
  };

  // Verificar si el usuario tiene el pago AMPA del año actual
  const checkAmpaPayment = async () => {
    try {
      setCheckingAmpaPayment(true);
      const currentUser = auth.currentUser;

      if (!currentUser) {
        console.log('No hay usuario autenticado');
        setHasAmpaPayment(false);
        return;
      }

      const currentAcademicYear = getCurrentAcademicYear();
      console.log('Verificando pago AMPA para:', currentUser.uid, 'Año académico:', currentAcademicYear);

      // Buscar en ampaPayments un documento con el userId del usuario actual
      const ampaPaymentsQuery = query(
        collection(db, 'ampaPayments'),
        where('userId', '==', currentUser.uid),
        where('academicYear', '==', currentAcademicYear),
        where('status', '==', 'completed')
      );

      const querySnapshot = await getDocs(ampaPaymentsQuery);

      if (!querySnapshot.empty) {
        console.log('✅ Usuario tiene pago AMPA completado del año actual');
        setHasAmpaPayment(true);
      } else {
        console.log('❌ Usuario NO tiene pago AMPA completado del año actual');
        setHasAmpaPayment(false);
      }
    } catch (error) {
      console.error('Error al verificar pago AMPA:', error);
      setHasAmpaPayment(false);
    } finally {
      setCheckingAmpaPayment(false);
    }
  };

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'events', eventId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const eventData = { id: docSnap.id, ...docSnap.data() };
        setEvent(eventData);
      } else {
        setError('Evento no encontrado');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      setError('Error al cargar el evento');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Fecha no especificada';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'No especificada';
    try {
      // Manejar Timestamp de Firestore
      if (timestamp.seconds) {
        const date = new Date(timestamp.seconds * 1000);
        return date.toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      // Manejar toDate (Firestore Timestamp)
      if (timestamp.toDate) {
        const date = timestamp.toDate();
        return date.toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      // Manejar string datetime-local (formato: "2024-12-25T15:30")
      if (typeof timestamp === 'string') {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      // Manejar objeto Date
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      return 'Fecha inválida';
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Fecha inválida';
    }
  };

  const getCategoryLabel = (category) => {
    const categories = {
      'reunion': 'Reunión',
      'evento': 'Evento',
      'actividad': 'Actividad',
      'celebracion': 'Celebración',
      'excursion': 'Excursión',
      'taller': 'Taller',
      'conferencia': 'Conferencia',
      'otros': 'Otros'
    };
    return categories[category] || category || 'Sin categoría';
  };

  const handleGoBack = () => {
    navigate('/portal');
  };

  const handleOpenPayment = () => {
    setPaymentDialogOpen(true);
  };

  // Calcular disponibilidad de entradas
  const getAvailableTickets = () => {
    if (!event || event.precio === 0) return null; // Eventos gratuitos sin límite
    const totalPass = event.totalpass || 0;
    const sold = event.sold || 0;
    return totalPass - sold;
  };

  const isEventSoldOut = () => {
    const available = getAvailableTickets();
    return available !== null && available <= 0;
  };

  const handleClosePayment = () => {
    setPaymentDialogOpen(false);
  };

  const handlePaymentSuccess = (paymentResult) => {
    console.log('Payment successful:', paymentResult);
    // Aquí puedes actualizar el estado, mostrar un mensaje, etc.
    setPaymentDialogOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: COLORS.accent }} size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: COLORS.background, py: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton
              onClick={handleGoBack}
              sx={{ mr: 2, color: COLORS.accent }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
              Evento
            </Typography>
          </Box>

          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: `linear-gradient(to bottom, ${COLORS.background}, ${COLORS.surface})`, py: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        {/* Header con animación */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: { xs: 2, sm: 3 },
          animation: 'fadeInDown 0.5s ease-in-out',
          '@keyframes fadeInDown': {
            '0%': {
              opacity: 0,
              transform: 'translateY(-20px)'
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}>
          <IconButton
            onClick={handleGoBack}
            sx={{
              mr: 2,
              color: COLORS.accent,
              backgroundColor: COLORS.primary + '30',
              '&:hover': {
                backgroundColor: COLORS.primary + '50',
                transform: 'scale(1.05)',
                transition: 'all 0.3s ease'
              }
            }}
            size={isMobile ? "small" : "medium"}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: 'bold',
              color: COLORS.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: { xs: 'nowrap', sm: 'normal' }
            }}
          >
            {event?.titulo}
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {/* Imagen del evento con efecto hover */}
          {event?.imagen && (
            <Grid item xs={12} md={6}>
              <Paper
                elevation={4}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  backgroundColor: COLORS.surface,
                  position: 'relative',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 24px ${COLORS.accent}40`
                  },
                  animation: 'fadeInLeft 0.6s ease-in-out',
                  '@keyframes fadeInLeft': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateX(-30px)'
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateX(0)'
                    }
                  }
                }}
              >
                <CardMedia
                  component="img"
                  sx={{
                    height: { xs: 250, sm: 350, md: 400 },
                    objectFit: 'cover',
                    width: '100%'
                  }}
                  image={event.imagen}
                  alt={event.titulo}
                />
                {/* Overlay con gradiente */}
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '50%',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                  display: 'flex',
                  alignItems: 'flex-end',
                  p: 3
                }}>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {event.titulo}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Información del evento con diseño mejorado */}
          <Grid item xs={12} md={event?.imagen ? 6 : 12}>
            <Paper
              elevation={4}
              sx={{
                p: { xs: 2.5, sm: 3.5, md: 4.5 },
                borderRadius: 4,
                background: `linear-gradient(135deg, ${COLORS.surface} 0%, ${COLORS.background} 100%)`,
                height: 'fit-content',
                border: `2px solid ${COLORS.primary}40`,
                position: 'relative',
                overflow: 'hidden',
                animation: 'fadeInRight 0.6s ease-in-out',
                '@keyframes fadeInRight': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateX(30px)'
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateX(0)'
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: `linear-gradient(to right, ${COLORS.accent}, ${COLORS.primary})`
                }
              }}
            >
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{
                  fontWeight: 'bold',
                  mb: 2.5,
                  color: COLORS.text.primary,
                  textShadow: `2px 2px 4px ${COLORS.primary}30`
                }}
              >
                {event?.titulo}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                {event?.categoria && (
                  <Chip
                    icon={<CategoryIcon />}
                    label={getCategoryLabel(event.categoria)}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                      color: 'white',
                      fontWeight: 'bold',
                      boxShadow: `0 4px 12px ${COLORS.primary}40`,
                      '&:hover': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.2s ease'
                      }
                    }}
                  />
                )}
                {event?.precio > 0 && (
                  <Chip
                    label={`${Number(event.precio).toFixed(2)}€`}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      background: `linear-gradient(135deg, ${COLORS.success}, ${COLORS.accent})`,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '0.875rem' : '1rem',
                      boxShadow: `0 4px 12px ${COLORS.success}40`,
                      '&:hover': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.2s ease'
                      }
                    }}
                  />
                )}
              </Box>

              <Divider sx={{ my: 3, borderColor: COLORS.primary + '30' }} />

              {/* Información detallada con estilo mejorado */}
              <List sx={{ '& .MuiListItem-root': { transition: 'all 0.3s ease' } }}>
                <ListItem sx={{
                  px: 0,
                  py: 2,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: COLORS.primary + '15',
                    transform: 'translateX(8px)'
                  }
                }}>
                  <ListItemIcon>
                    <Box sx={{
                      backgroundColor: COLORS.accent + '20',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CalendarIcon sx={{ color: COLORS.accent, fontSize: 28 }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary="Fecha de inicio"
                    secondary={formatDateTime(event?.fechaInicio)}
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: 'bold',
                      color: COLORS.text.primary
                    }}
                    secondaryTypographyProps={{
                      variant: 'body1',
                      color: COLORS.text.secondary,
                      fontWeight: 500
                    }}
                  />
                </ListItem>

                {event?.fechaFin && (
                  <ListItem sx={{
                    px: 0,
                    py: 2,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: COLORS.primary + '15',
                      transform: 'translateX(8px)'
                    }
                  }}>
                    <ListItemIcon>
                      <Box sx={{
                        backgroundColor: COLORS.secondary + '20',
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <EventIcon sx={{ color: COLORS.secondary, fontSize: 28 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary="Fecha de fin"
                      secondary={formatDateTime(event.fechaFin)}
                      primaryTypographyProps={{
                        variant: 'body1',
                        fontWeight: 'bold',
                        color: COLORS.text.primary
                      }}
                      secondaryTypographyProps={{
                        variant: 'body1',
                        color: COLORS.text.secondary,
                        fontWeight: 500
                      }}
                    />
                  </ListItem>
                )}

                {event?.ubicacion && (
                  <ListItem sx={{
                    px: 0,
                    py: 2,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: COLORS.primary + '15',
                      transform: 'translateX(8px)'
                    }
                  }}>
                    <ListItemIcon>
                      <Box sx={{
                        backgroundColor: COLORS.success + '20',
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <LocationIcon sx={{ color: COLORS.success, fontSize: 28 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary="Ubicación"
                      secondary={event.ubicacion}
                      primaryTypographyProps={{
                        variant: 'body1',
                        fontWeight: 'bold',
                        color: COLORS.text.primary
                      }}
                      secondaryTypographyProps={{
                        variant: 'body1',
                        color: COLORS.text.secondary,
                        fontWeight: 500
                      }}
                    />
                  </ListItem>
                )}

                {event?.organizador && (
                  <ListItem sx={{
                    px: 0,
                    py: 2,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: COLORS.primary + '15',
                      transform: 'translateX(8px)'
                    }
                  }}>
                    <ListItemIcon>
                      <Box sx={{
                        backgroundColor: COLORS.accent + '20',
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <PersonIcon sx={{ color: COLORS.accent, fontSize: 28 }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary="Organizador"
                      secondary={event.organizador}
                      primaryTypographyProps={{
                        variant: 'body1',
                        fontWeight: 'bold',
                        color: COLORS.text.primary
                      }}
                      secondaryTypographyProps={{
                        variant: 'body1',
                        color: COLORS.text.secondary,
                        fontWeight: 500
                      }}
                    />
                  </ListItem>
                )}
              </List>

              {event?.descripcion && (
                <Box sx={{ mt: 4 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2.5,
                    pb: 1,
                    borderBottom: `2px solid ${COLORS.accent}40`
                  }}>
                    <Box sx={{
                      backgroundColor: COLORS.accent + '20',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <DescriptionIcon sx={{ color: COLORS.accent, fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                      Descripción
                    </Typography>
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      background: `linear-gradient(135deg, ${COLORS.background} 0%, ${COLORS.primary}15 100%)`,
                      borderRadius: 3,
                      border: `1px solid ${COLORS.primary}30`,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        background: `linear-gradient(to bottom, ${COLORS.accent}, ${COLORS.primary})`
                      }
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: COLORS.text.secondary,
                        lineHeight: 1.9,
                        whiteSpace: 'pre-wrap',
                        fontSize: '1rem',
                        pl: 1.5
                      }}
                    >
                      {event.descripcion}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Información adicional con diseño mejorado */}
              {(event?.precio || event?.capacidad || event?.requiereInscripcion) && (
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ mb: 3, borderColor: COLORS.primary + '40' }} />
                  <Typography variant="h6" sx={{
                    fontWeight: 'bold',
                    mb: 3,
                    color: COLORS.text.primary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Box sx={{
                      width: 6,
                      height: 24,
                      background: `linear-gradient(to bottom, ${COLORS.accent}, ${COLORS.primary})`,
                      borderRadius: 1
                    }} />
                    Información adicional
                  </Typography>

                  <Grid container spacing={2.5}>
                    {event?.precio !== undefined && (
                      <Grid item xs={12} sm={6}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            background: `linear-gradient(135deg, ${COLORS.success}15 0%, ${COLORS.success}05 100%)`,
                            borderRadius: 3,
                            textAlign: 'center',
                            border: `2px solid ${COLORS.success}30`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: `0 8px 20px ${COLORS.success}30`
                            }
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                            💰 Precio
                          </Typography>
                          <Typography variant="h5" sx={{
                            fontWeight: 'bold',
                            color: COLORS.success,
                            textShadow: `1px 1px 2px ${COLORS.success}20`
                          }}>
                            {event.precio === 0 ? 'Gratuito' : `${event.precio}€`}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {/* Disponibilidad de entradas - Solo para eventos de pago */}
                    {event?.precio > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            background: isEventSoldOut()
                              ? `linear-gradient(135deg, #EF535015 0%, #EF535005 100%)`
                              : `linear-gradient(135deg, ${COLORS.accent}15 0%, ${COLORS.accent}05 100%)`,
                            borderRadius: 3,
                            textAlign: 'center',
                            border: isEventSoldOut()
                              ? `2px solid #EF535030`
                              : `2px solid ${COLORS.accent}30`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: isEventSoldOut()
                                ? `0 8px 20px #EF535030`
                                : `0 8px 20px ${COLORS.accent}30`
                            }
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                            🎟️ Disponibilidad
                          </Typography>
                          <Typography variant="h5" sx={{
                            fontWeight: 'bold',
                            color: isEventSoldOut() ? '#EF5350' : COLORS.accent,
                            textShadow: isEventSoldOut()
                              ? `1px 1px 2px #EF535020`
                              : `1px 1px 2px ${COLORS.accent}20`
                          }}>
                            {isEventSoldOut()
                              ? 'Agotadas'
                              : `${getAvailableTickets()} disponibles`
                            }
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {event?.capacidad && (
                      <Grid item xs={12} sm={6}>
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            background: `linear-gradient(135deg, ${COLORS.accent}15 0%, ${COLORS.accent}05 100%)`,
                            borderRadius: 3,
                            textAlign: 'center',
                            border: `2px solid ${COLORS.accent}30`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: `0 8px 20px ${COLORS.accent}30`
                            }
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                            👥 Capacidad
                          </Typography>
                          <Typography variant="h5" sx={{
                            fontWeight: 'bold',
                            color: COLORS.accent,
                            textShadow: `1px 1px 2px ${COLORS.accent}20`
                          }}>
                            {event.capacidad} personas
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>

                  {event?.requiereInscripcion && (
                    <Alert
                      severity="info"
                      sx={{
                        mt: 2.5,
                        borderRadius: 2,
                        backgroundColor: COLORS.accent + '10',
                        border: `1px solid ${COLORS.accent}40`,
                        '& .MuiAlert-icon': {
                          color: COLORS.accent
                        }
                      }}
                    >
                      Este evento requiere inscripción previa. Contacta con la administración del AMPA para más información.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Botón de inscripción/pago mejorado - Solo visible si el evento está activo */}
              {event?.precio > 0 && event?.active !== false && (
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ mb: 3, borderColor: COLORS.primary + '40' }} />

                  {isEventSoldOut() && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Lo sentimos, las entradas para este evento están agotadas.
                      </Typography>
                    </Alert>
                  )}

                  {!hasAmpaPayment && !checkingAmpaPayment && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ⚠️ Pago AMPA requerido
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Necesitas tener el pago del AMPA del curso actual ({getCurrentAcademicYear()}) completado para poder inscribirte a este evento.
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        💡 Contacta con la administración del AMPA para realizar el pago.
                      </Typography>
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    size={isMobile ? "medium" : "large"}
                    startIcon={<PaymentIcon />}
                    onClick={handleOpenPayment}
                    disabled={isEventSoldOut() || !hasAmpaPayment || checkingAmpaPayment}
                    sx={{
                      background: isEventSoldOut()
                        ? '#BDBDBD'
                        : `linear-gradient(135deg, ${COLORS.success} 0%, ${COLORS.accent} 100%)`,
                      color: 'white',
                      py: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '1.1rem', sm: '1.2rem' },
                      fontWeight: 'bold',
                      borderRadius: 3,
                      boxShadow: isEventSoldOut()
                        ? 'none'
                        : `0 6px 20px ${COLORS.success}40`,
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                        transition: 'left 0.5s ease'
                      },
                      '&:hover': {
                        background: isEventSoldOut()
                          ? '#BDBDBD'
                          : `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.success} 100%)`,
                        transform: isEventSoldOut() ? 'none' : 'translateY(-2px)',
                        boxShadow: isEventSoldOut()
                          ? 'none'
                          : `0 8px 25px ${COLORS.success}50`,
                        '&::before': {
                          left: isEventSoldOut() ? '-100%' : '100%'
                        }
                      },
                      '&:active': {
                        transform: isEventSoldOut() ? 'none' : 'translateY(0)'
                      },
                      '&.Mui-disabled': {
                        color: 'white',
                        opacity: 0.6
                      }
                    }}
                  >
                    {checkingAmpaPayment
                      ? 'Verificando pago AMPA...'
                      : isEventSoldOut()
                      ? '❌ Entradas agotadas'
                      : !hasAmpaPayment
                      ? '🔒 Pago AMPA requerido'
                      : (isMobile ? '🎟️ Inscribirse' : '🎟️ Inscribirse al evento')
                    }
                  </Button>
                </Box>
              )}

              {/* Mensaje si el evento está inactivo */}
              {event?.active === false && (
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ mb: 3, borderColor: COLORS.primary + '40' }} />
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Este evento no está disponible actualmente para inscripciones.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Dialog de pago con Stripe */}
      <Elements stripe={stripePromise}>
        <EventPaymentDialog
          open={paymentDialogOpen}
          onClose={handleClosePayment}
          event={event}
          onPaymentSuccess={handlePaymentSuccess}
        />
      </Elements>
    </Box>
  );
};

export default UserEventDetail;